import { useCallback, useContext, useMemo } from 'react';
import { ModerationContext } from '@/providers/ModerationProvider';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { ImageDimension } from '@/type/detection.types';

type BoxCoordinatesProps = {
  isModerated?: boolean;
  element?: TResultImageCoordinatesSchema;
  imageDimension?: ImageDimension;
};

type PolygonPointsProps = {
  element?: TResultImageCoordinatesSchema;
};

type DenormalizePolygonPointsProps = {
  originalBoundingBox?: Array<number>;
  element?: TResultImageCoordinatesSchema;
};

type PolygonScalePointsProps = {
  isModerated?: boolean;
  polygonPoints?: { x: number; y: number }[];
  denormalizePolygonPoints?: () => { x: number; y: number }[];
  imageDimension?: ImageDimension;
};

export const useModerate = () => {
  const context = useContext(ModerationContext);

  if (!context) {
    throw new Error('useModerate must be used within an ModerateProvider');
  }

  return context;
};

export const useEngineCorrection = () => {
  const context = useContext(EngineCorrectionContext);

  if (!context) {
    throw new Error(
      'useModerate must be used within an EngineCorrectionProvider'
    );
  }

  return context;
};

export const useBoxCoordinates = ({
  isModerated,
  element,
  imageDimension,
}: BoxCoordinatesProps) => {
  const getCoordinates = useCallback(() => {
    if (!element) {
      return null;
    }

    let x = 0;
    let y = 0;
    let width = 0;
    let height = 0;

    if (isModerated) {
      x = element?.xCenter * 100 - (element?.width * 100) / 2;
      y = element?.yCenter * 100 - (element?.height * 100) / 2;
      width = element?.width * 100;
      height = element?.height * 100;
    } else {
      x = (element?.xCenter / imageDimension?.width) * 100;
      y = (element?.yCenter / imageDimension?.height) * 100;

      const actualWidth = element?.width - element?.xCenter;
      const actualHeight = Math.abs(element?.height - element?.yCenter);
      width = (actualWidth / imageDimension?.width) * 100;
      height = (actualHeight / imageDimension?.height) * 100;
    }

    return {
      x,
      y,
      width,
      height,
    };
  }, [isModerated, element, imageDimension]);

  return { getCoordinates };
};

export const usePolygonPoints = ({ element }: PolygonPointsProps) => {
  const getPoints = useMemo(() => {
    if (
      !element?.isDamaged ||
      !Array.isArray(element?.point) ||
      element?.point.length === 0
    )
      return null;

    return element?.point?.map((point) => ({
      x: point.x * 100,
      y: point.y * 100,
    }));
  }, [element?.isDamaged, element?.point]);

  return { getPoints };
};

export const useDenormalizePolygonPoints = ({
  originalBoundingBox,
  element,
}: DenormalizePolygonPointsProps) => {
  const denormalizePoints = useCallback(() => {
    const x1_originalBBox = originalBoundingBox?.[0];
    const y1_originalBBox = originalBoundingBox?.[1];
    const x2_originalBBox = originalBoundingBox?.[2];
    const y2_originalBBox = originalBoundingBox?.[3];

    const width = x2_originalBBox - x1_originalBBox;
    const height = y2_originalBBox - y1_originalBBox;

    return element?.point?.map((point) => ({
      x: x1_originalBBox + point?.x * width,
      y: y1_originalBBox + point?.y * height,
    }));
  }, [element?.point, originalBoundingBox]);

  return { denormalizePoints };
};

export const usePolygonScalePoints = ({
  isModerated,
  polygonPoints,
  denormalizePolygonPoints,
  imageDimension,
}: PolygonScalePointsProps) => {
  const getScalePoints = useCallback(
    (canvas) => {
      if (isModerated) {
        return polygonPoints.map((point) => ({
          x: (point.x / 100) * canvas.width,
          y: (point.y / 100) * canvas.height,
        }));
      } else {
        const denormalized = denormalizePolygonPoints();
        return denormalized.map((point) => ({
          x: (point.x / imageDimension?.width) * canvas.width,
          y: (point.y / imageDimension?.height) * canvas.height,
        }));
      }
    },
    [
      isModerated,
      polygonPoints,
      denormalizePolygonPoints,
      imageDimension?.height,
      imageDimension?.width,
    ]
  );

  return { getScalePoints };
};
